// Le fasi del progetto e il cronoprogramma: ogni fase ha cosa si fa, chi la guida, con quale
// modello e quanti giorni lavorativi; le date seguono dalla data di inizio, una fase dopo
// l'altra. La pipeline è quella con cui il PM fa girare i lavori.
import { Button } from '../../../components/Button';
import { Field, TextField } from '../../../components/Field';
import { Pop } from '../../../components/Pop';
import { Select } from '../../../components/Select';
import type {
  ConnectedProvider,
  Pipeline,
  PlanMember,
  PlanPhase,
  ProjectPlan,
} from '../../../lib/api';
import { ModelPair } from '../../agents/ModelPair';
import { schedule, shortDay } from './plan';

export function PhasesEditor({
  plan,
  onChange,
  team,
  providers,
  pipelines,
}: {
  plan: ProjectPlan;
  onChange: (patch: Partial<ProjectPlan>) => void;
  team: PlanMember[];
  providers: ConnectedProvider[];
  pipelines: Pipeline[];
}) {
  const dates = schedule(plan);
  const total = plan.phases.reduce((n, p) => n + p.days, 0);
  const put = (i: number, patch: Partial<PlanPhase>) =>
    onChange({ phases: plan.phases.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const avatarOf = (name: string) => team.find((m) => m.name === name)?.avatar ?? null;

  return (
    <section className="np-card">
      <header className="np-card-head">
        <h2 className="t-overline">Fasi e cronoprogramma</h2>
        <span className="t-caption faint">
          {total} giorni lavorativi
          {dates.length
            ? ` · ${shortDay(dates[0].from)} → ${shortDay(dates[dates.length - 1].to)}`
            : ''}
        </span>
      </header>
      <div className="np-two">
        <Field label="Inizio" hint="Sabato e domenica non contano.">
          <input
            type="date"
            className="np-date"
            value={plan.start ?? ''}
            onChange={(e) => onChange({ start: e.target.value || null })}
          />
        </Field>
        <Field label="Pipeline">
          <Select
            label="Pipeline"
            value={plan.pipeline ?? ''}
            onChange={(v) => onChange({ pipeline: v || null })}
            options={[
              { value: '', label: 'Quella predefinita' },
              ...pipelines.map((p) => ({ value: p.id, label: p.name })),
            ]}
            width="100%"
          />
        </Field>
      </div>
      {plan.phases.map((p, i) => {
        const owner = avatarOf(p.owner);
        const span = dates[i];
        const offset = plan.phases.slice(0, i).reduce((n, x) => n + x.days, 0);
        return (
          <div key={i} className="np-phase">
            <span className="np-num">{i + 1}</span>
            <div className="np-phase-body">
              <div className="np-phase-line">
                <TextField value={p.name} onChange={(v) => put(i, { name: v })} />
                <Select
                  label={`Chi guida ${p.name}`}
                  value={p.owner}
                  onChange={(v) => put(i, { owner: v })}
                  options={[
                    { value: '', label: 'Nessuno in particolare' },
                    ...team.map((m) => ({ value: m.name, label: m.name })),
                  ]}
                  width="100%"
                />
                <ModelPair
                  providers={providers}
                  who={`della fase ${p.name}`}
                  value={p.model}
                  onChange={(model) => put(i, { model })}
                  followsFrom="dai pops"
                  followsName="Quello dei pops"
                />
                <TextField
                  value={String(p.days)}
                  onChange={(v) => put(i, { days: Math.max(1, Math.min(60, Number(v) || 1)) })}
                />
                <Button
                  variant="icon"
                  icon="x"
                  aria-label={`Togli la fase ${p.name}`}
                  onClick={() => onChange({ phases: plan.phases.filter((_, j) => j !== i) })}
                />
              </div>
              <TextField
                value={p.description}
                onChange={(v) => put(i, { description: v })}
                placeholder="Cosa succede in questa fase"
              />
              <div className="np-bar-track" aria-hidden="true">
                <span
                  className="np-bar"
                  style={{
                    left: `${(offset / Math.max(total, 1)) * 100}%`,
                    width: `${(p.days / Math.max(total, 1)) * 100}%`,
                  }}
                >
                  {owner && <Pop avatar={owner} size={20} />}
                  {span && `${shortDay(span.from)} → ${shortDay(span.to)}`}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      <div>
        <Button
          size="sm"
          icon="plus"
          onClick={() =>
            onChange({
              phases: [
                ...plan.phases,
                { name: 'Nuova fase', description: '', owner: '', model: null, days: 1 },
              ],
            })
          }
        >
          Aggiungi fase
        </Button>
      </div>
    </section>
  );
}
